/**
 * The raw sessions under the landings aggregate — one row per session, with
 * where it landed, what it did, the device, and the account's first touch.
 *
 * Sorted and filtered HERE, over every row, before a page is cut: DataTable
 * reorders only the rows it is handed, so sorting after paging would sort one
 * page of fifty and call it the table.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { withReturnTo } from '@shared-ui/navigation';
import { useQuery } from '@tanstack/react-query';
import { campaignsApi } from '../../../api/campaignsApi';
import SessionJourneyDialog from './SessionJourneyDialog';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  DataTable, InfoTip, SearchFilterBar, useColumnVisibility, type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import type { LandingSession } from '../../../api/siteAnalyticsApi';
import { firstTouchLabel, firstTouchParts, referrerKindLabel, siteLabel } from './sourceLabels';

const PAGE_SIZE = 50;

/** `phone` is the stored word; `mobile` is what the science site wrote for its
 *  first hours of recording, and means the same. */
const DEVICE_LABELS: Record<string, string> = { phone: 'Phone', mobile: 'Phone', tablet: 'Tablet', desktop: 'Desktop' };
const OS_LABELS: Record<string, string> = {
  ios: 'iOS', android: 'Android', windows: 'Windows', macos: 'macOS', chromeos: 'ChromeOS', linux: 'Linux', other: 'Other',
};
/** The app whose built-in browser a session ran in. */
const IN_APP_LABELS: Record<string, string> = {
  instagram: 'Instagram app', facebook: 'Facebook app', messenger: 'Messenger', linkedin: 'LinkedIn app',
  snapchat: 'Snapchat', tiktok: 'TikTok', twitter: 'X app', '(own browser)': 'Own browser',
};
const ACCOUNT_LABELS: Record<LandingSession['account'], string> = { guest: 'Guest', 'signed-in': 'Signed in', none: 'No account' };

/** Who arrived: the account's kind by whether it is new in the window. */
const WHO = [
  { value: 'guest:new', label: 'Guest · new' },
  { value: 'guest:returning', label: 'Guest · returning' },
  { value: 'signed-in:new', label: 'Signed in · new' },
  { value: 'signed-in:returning', label: 'Signed in · returning' },
];

/** `09/25 12:54` — month/day, local time, 24-hour, no year: the column is read
 *  down, and the full timestamp is on hover. Built by hand because locale
 *  formatting adds "Sept," or "at … PM" and doubles the width. */
function fmtTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Column priority, most useful first. The table is wider than a laptop, so the
 * order decides what falls off the right edge — who came, from where and how,
 * before the mechanics. DETAIL columns start hidden; the picker shows them.
 */
const COLUMN_PRIORITY = [
  'landedAt', 'site', 'landing', 'viaLink', 'source', 'campaign', 'account', 'userId',
  'did', 'device', 'pageViews', 'duration', 'referrer',
  'medium', 'placement', 'link', 'adClick', 'lastPage', 'host',
];
const DETAIL_COLUMNS = new Set(['medium', 'placement', 'link', 'adClick', 'lastPage', 'host']);
const rank = (key: string) => {
  const i = COLUMN_PRIORITY.indexOf(key);
  return i < 0 ? COLUMN_PRIORITY.length : i;
};
function fmtDuration(ms?: number): string {
  if (ms === undefined) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}
const referrerLabel = referrerKindLabel;

/** A value to filter and sort on for each filter key. */

const FILTER_VALUE: Record<string, (s: LandingSession) => string> = {
  site: s => s.site ?? 'science',
  device: s => s.device ?? '(not recorded)',
  os: s => s.os ?? '(not recorded)',
  inApp: s => s.inApp ?? '(own browser)',
  who: s => (s.account === 'none' || !s.returning ? '(no account)' : `${s.account}:${s.returning}`),
  referrer: s => s.referrer,
  source: s => s.firstTouch?.source ?? '(no account)',
  campaign: s => s.firstTouch?.campaign ?? '(none)',
  viaLink: s => s.viaLink?.slug ?? '(none)',
  did: s => (s.answers ? 'answered' : s.did.bounce ? 'bounce' : s.did.lab ? 'lab' : s.did.simulation ? 'simulation' : 'browsed'),
};

const SORT_VALUE: Record<string, (s: LandingSession) => string | number> = {
  landedAt: s => s.landedAt,
  site: s => s.site ?? 'science',
  landing: s => s.landing,
  pageViews: s => s.pageViews,
  duration: s => s.durationMs ?? -1,
  device: s => s.device ?? '',
  referrer: s => s.referrer,
  account: s => s.account,
  userId: s => s.userLabel ?? s.userId ?? '',
  source: s => s.firstTouch?.source ?? '',
  medium: s => s.firstTouch?.medium ?? '',
  campaign: s => s.firstTouch?.campaign ?? '',
  placement: s => s.firstTouch?.placement ?? '',
  link: s => s.firstTouch?.link ?? '',
  lastPage: s => s.lastPage ?? '',
  host: s => s.host ?? '',
  viaLink: s => s.viaLink?.slug ?? '',
};

function Dash() { return <span className="sa-zero">—</span>; }

/** The four lines: each site, split by new and returning account. */
const TREND_LINES = [
  { key: 'science:new', name: 'Science site · new', color: '#10b981' },
  { key: 'science:returning', name: 'Science site · returning', color: '#6ee7b7' },
  { key: 'app:new', name: 'BuzzBrain app · new', color: '#6366f1' },
  { key: 'app:returning', name: 'BuzzBrain app · returning', color: '#a5b4fc' },
] as const;

/**
 * Sessions over time, from the rows the table shows — so its filters and
 * search narrow the chart too. By UTC day, or by hour when the window is two
 * days or less. A session with no account is neither new nor returning and is
 * in no line.
 */
function SessionsTrend({ sessions, from, to }: { sessions: LandingSession[]; from?: Date; to?: Date }) {
  const hourly = !!from && !!to && to.getTime() - from.getTime() <= 2 * 86400_000;
  const data = useMemo(() => {
    const len = hourly ? 13 : 10;
    const buckets = new Map<string, Record<string, number>>();
    for (const s of sessions) {
      if (!s.returning) continue;
      const b = s.landedAt.slice(0, len);
      const row = buckets.get(b) ?? {};
      const k = `${s.site ?? 'science'}:${s.returning}`;
      row[k] = (row[k] ?? 0) + 1;
      buckets.set(b, row);
    }
    const label = (b: string) => (hourly
      ? new Date(`${b}:00:00Z`).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
      : new Date(`${b}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' }));
    return [...buckets.entries()].sort(([a], [b]) => a.localeCompare(b))
      .map(([b, row]) => ({ label: label(b), ...Object.fromEntries(TREND_LINES.map(l => [l.key, row[l.key] ?? 0])) }));
  }, [sessions, hourly]);

  if (data.length < 2) return null;
  return (
    <div className="sa-trend">
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
          <CartesianGrid stroke="var(--border-color)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} minTickGap={24} />
          <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} allowDecimals={false} />
          <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {TREND_LINES.map(l => (
            <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function LandingSessionsTable({ sessions, truncated, title = 'Every session', from, to }: {
  sessions: LandingSession[]; truncated?: number; title?: string;
  /** The window, when known — it decides whether the chart is by hour or day. */
  from?: Date; to?: Date;
}) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState>({ sortBy: 'landedAt', sortOrder: 'desc' });
  const [page, setPage] = useState(1);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [open, setOpen] = useState<LandingSession | null>(null);
  // Names typed for campaign values that are ids (Acquisition → Campaigns).
  const campaignNames = useQuery({ queryKey: ['campaign-names'], queryFn: campaignsApi.names, staleTime: 5 * 60_000 });
  const names = campaignNames.data?.names;
  const nameOf = useCallback((c: string) => names?.[c] ?? c, [names]);

  // Testers' and seeded accounts' sessions arrive flagged; hidden unless asked
  // for, and counted beside the switch so a missing visit has an explanation.
  const hiddenInternal = useMemo(() => sessions.filter(s => s.internal).length, [sessions]);
  const base = useMemo(
    () => (includeInternal ? sessions : sessions.filter(s => !s.internal)),
    [sessions, includeInternal],
  );

  // Options come from the rows, with their counts, so a filter never offers a
  // value nobody has.
  const filterDefs = useMemo<FilterDef[]>(() => {
    const opts = (key: string, label: (v: string) => string = v => v) => {
      const n = new Map<string, number>();
      for (const s of base) { const v = FILTER_VALUE[key](s); n.set(v, (n.get(v) ?? 0) + 1); }
      return [...n.entries()].sort((a, b) => b[1] - a[1]).map(([v, c]) => ({ value: v, label: `${label(v)} (${c})` }));
    };
    return [
      { key: 'site', kind: 'select', placeholder: 'Both sites', options: opts('site', v => siteLabel(v)) },
      { key: 'device', kind: 'select', placeholder: 'Any device', options: opts('device', v => DEVICE_LABELS[v] ?? v) },
      { key: 'os', kind: 'select', placeholder: 'Any OS', options: opts('os', v => OS_LABELS[v] ?? v) },
      { key: 'inApp', kind: 'select', placeholder: 'Any browser', options: opts('inApp', v => IN_APP_LABELS[v] ?? v) },
      // Account kind and new-vs-returning are one question — who arrived — so
      // one filter, its four answers in a fixed order rather than by count.
      { key: 'who', kind: 'select', placeholder: 'Anyone', options: WHO.map(w => ({
        value: w.value, label: `${w.label} (${base.filter(x => FILTER_VALUE.who(x) === w.value).length})`,
      })) },
      { key: 'referrer', kind: 'select', placeholder: 'Any referrer', options: opts('referrer', referrerLabel) },
      { key: 'source', kind: 'select', placeholder: 'Any source', options: opts('source', firstTouchLabel) },
      { key: 'campaign', kind: 'select', placeholder: 'Any campaign', options: opts('campaign', nameOf) },
      { key: 'viaLink', kind: 'select', placeholder: 'Any link', options: opts('viaLink') },
      { key: 'did', kind: 'select', placeholder: 'Did anything', options: opts('did') },
    ];
  }, [base, nameOf]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = Object.entries(filters).filter(([, v]) => v);
    const rows = base.filter(s =>
      active.every(([k, v]) => FILTER_VALUE[k]?.(s) === v)
      && (!q || [s.landing, s.lastPage, s.userId, s.sessionId, s.referrerHost, s.firstTouch?.campaign,
        s.firstTouch?.source, s.firstTouch?.link, s.viaLink?.slug, s.userLabel].some(x => x?.toLowerCase().includes(q))));
    const read = SORT_VALUE[sort.sortBy] ?? SORT_VALUE.landedAt;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = read(a), y = read(b);
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
  }, [base, filters, query, sort]);

  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageRows = shown.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const col = (key: string, header: string, render: (s: LandingSession) => React.ReactNode,
    extra: Partial<ColumnDef<LandingSession>> = {}): ColumnDef<LandingSession> =>
    ({ key, header, sortable: key in SORT_VALUE, render, ...extra });

  const allColumns: ColumnDef<LandingSession>[] = [
    col('landedAt', 'Landed', s => <span className="lp-nowrap" title={s.landedAt}>{fmtTime(s.landedAt)}</span>,
      { hideable: false }),
    col('site', 'Site', s => siteLabel(s.site ?? 'science')),
    col('landing', 'Landing page', s => <span className="lp-page">{s.landing}</span>),
    col('pageViews', 'Pages', s => s.pageViews, { className: 'sa-num' }),
    col('duration', 'Time', s => <span className="lp-nowrap">{fmtDuration(s.durationMs)}</span>, { className: 'sa-num' }),
    col('did', 'Did', s => (
      <span className="lp-did">
        {s.did.simulation && <span className="lp-chip">sim</span>}
        {s.did.lab && <span className="lp-chip">lab</span>}
        {s.did.bounce && <span className="lp-chip is-muted">1 page</span>}
        {!!s.answers && <span className="lp-chip">{s.answers} {s.answers === 1 ? 'answer' : 'answers'}</span>}
      </span>
    )),
    col('device', 'Device', s => (s.device
      ? <span className="lp-nowrap">
          {DEVICE_LABELS[s.device] ?? s.device}{s.os && ` · ${OS_LABELS[s.os] ?? s.os}`}
          {s.inApp && <span className="lp-chip is-muted" title="Opened inside this app's own browser">{IN_APP_LABELS[s.inApp] ?? s.inApp}</span>}
        </span>
      : <span className="sa-zero" title="Device is recorded on sessions from 25 Sep 2026">—</span>)),
    col('referrer', 'Referrer', s => <span title={s.referrerHost}>{referrerLabel(s.referrer)}</span>),
    col('viaLink', 'Via link', s => (s.viaLink
      ? <span className="lp-nowrap" title={s.viaLink.campaign ? `campaign ${s.viaLink.campaign}` : undefined}>
          {s.viaLink.slug}{s.viaLink.placement && <span className="lp-chip is-muted">{s.viaLink.placement}</span>}
        </span>
      : <Dash />)),
    col('account', 'Account', s => (
      <span className="lp-nowrap">
        {ACCOUNT_LABELS[s.account]}
        {s.returning && <span className={`lp-chip${s.returning === 'new' ? '' : ' is-muted'}`}>{s.returning}</span>}
        {s.account === 'guest' && s.signedUpAt && s.signedUpAt > s.landedAt && (
          <span className="lp-chip" title={`Signed up ${new Date(s.signedUpAt).toLocaleString()}${s.signedUpVia ? ` with ${s.signedUpVia}` : ''}`}>
            signed up later
          </span>
        )}
        {s.internal && <span className="lp-chip is-internal" title="A tester's or seeded account — in no figure on this page">internal</span>}
      </span>
    )),
    col('userId', 'User', s => (s.userId
      ? <Link to={`/users/${encodeURIComponent(s.userId)}`} {...withReturnTo({ label: 'Site usage' })}
          className="lp-user" title={s.userId}
          onClick={e => e.stopPropagation()}>{s.userLabel ?? `${s.userId.slice(0, 8)}…`}</Link>
      : <Dash />)),
    // The readable name in the cell; the raw value behind it — an app id, how an
    // account was provisioned — in the info tip, hover to peek, click to pin.
    col('source', 'Source', s => {
      if (!s.firstTouch?.source) return <Dash />;
      const { label, detail } = firstTouchParts(s.firstTouch.source);
      return (
        <span className="lp-source" onClick={e => e.stopPropagation()}>
          {label}{detail && <InfoTip text={detail} />}
        </span>
      );
    }),
    col('medium', 'Medium', s => s.firstTouch?.medium ?? <Dash />, { hideable: true, defaultHidden: true }),
    col('campaign', 'Campaign', s => (s.firstTouch?.campaign
      ? <span title={s.firstTouch.campaign}>{nameOf(s.firstTouch.campaign)}</span>
      : <Dash />)),
    col('placement', 'Placement', s => s.firstTouch?.placement ?? <Dash />, { hideable: true, defaultHidden: true }),
    col('link', 'Link', s => s.firstTouch?.link ?? <Dash />, { hideable: true }),
    col('adClick', 'Ad click', s => (s.firstTouch?.adClick
      ? <span className="lp-chip" title={`The first touch carried ${s.firstTouch.adClick}`}>{s.firstTouch.adClick}</span>
      : <Dash />), { hideable: true, defaultHidden: true }),
    col('lastPage', 'Left from', s => (s.lastPage ? <span className="lp-page">{s.lastPage}</span> : <Dash />),
      { hideable: true, defaultHidden: true }),
    col('host', 'Host', s => s.host ?? <Dash />, { hideable: true, defaultHidden: true }),
  ];
  const columns = [...allColumns]
    .sort((a, b) => rank(a.key) - rank(b.key))
    .map(c => (DETAIL_COLUMNS.has(c.key) ? { ...c, defaultHidden: true } : c));
  // The picker lives in the filter row, not the table's last header cell —
  // that cell is off-screen whenever the table is wider than the window.
  // Remembered on this device.
  const { visibleColumns, columnMenu } = useColumnVisibility(columns, 'site-usage.every-session.v2');

  return (
    <section className="sa-panel">
      {/* The definitions live behind the icon: they are read once, and the
          table is what the section is for. */}
      <h2>
        {title}
        <InfoTip>
          <span>
            One row per session, newest first. <strong>Source</strong>, <strong>Campaign</strong> and the rest are the
            account’s <em>first</em> touch — where this person came from the first time — so a returning person’s later
            session shows how they originally arrived, not how they got here today. <strong>Referrer</strong> and
            <strong> Via link</strong> are this session’s own: the published link it was reached through, if any. An app
            session records no referrer, so it reads “No referrer”. <strong>Account</strong> is its state now. App
            sessions are recorded only while session tracing is on.
          </span>
        </InfoTip>
      </h2>
      <SearchFilterBar filters={filterDefs} values={filters}
        onChange={v => { setFilters(v); setPage(1); }} hideSearch hideFetch>
        <label className="lp-toggle" title="Testers' and seeded accounts' sessions. Every figure on this page leaves them out.">
          <input type="checkbox" checked={includeInternal}
            onChange={e => { setIncludeInternal(e.target.checked); setPage(1); }} />
          Include internal accounts
          {!includeInternal && hiddenInternal > 0 && <span className="lp-hidden-n">({hiddenInternal} hidden)</span>}
        </label>
      </SearchFilterBar>
      <SessionsTrend sessions={shown} from={from} to={to} />
      <DataTable<LandingSession>
        columns={visibleColumns}
        data={pageRows}
        rowKey={s => s.sessionId}
        rowClassName={s => (s.internal ? 'lp-row-internal' : '')}
        onRowClick={setOpen}
        // The column picker beside the pager: both are about this table, and
        // the filter bar above is about what is in it.
        toolbarRight={columnMenu}
        sort={sort}
        onSortChange={s => { setSort(s); setPage(1); }}
        onSearch={q => { setQuery(q); setPage(1); }}
        searchPlaceholder="Search page, user id, campaign, referrer…"
        pagination={{ page: current, totalPages, total: shown.length, onPageChange: setPage }}
        emptyMessage="No session matches."
        // No `columnToggleKey` here: the picker is `columnMenu` in the filter
        // row above, and `columns` is already what it left visible. Giving the
        // table its own key as well draws a second picker in the last header
        // cell, over only the visible columns — two pickers that disagree.
        compact
      />
      <SessionJourneyDialog session={open} onClose={() => setOpen(null)} />
      {truncated ? (
        <p className="sa-note acq-aside">
          Showing the newest {sessions.length.toLocaleString()} sessions; {truncated.toLocaleString()} older ones in
          this window are not listed. Narrow the window to see them.
        </p>
      ) : null}
    </section>
  );
}
